import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/utils/store';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import CurrencyInput from '@/components/ui/CurrencyInput';
import MemberMultiSelect from '@/components/ui/MemberMultiSelect';
import { useFormDraft } from '@/hooks/useFormDraft';

const EMOJI_OPTIONS = ['🎯', '🏖️', '🚗', '🏠', '💻', '📱', '🎓', '💍', '🛡️', '✈️', '🎸', '💰'];

export default function AddGoalModal({ open, onClose, goal }) {
  const { t } = useTranslation();
  const addGoal = useStore((s) => s.addGoal);
  const updateGoal = useStore((s) => s.updateGoal);
  const isEdit = !!goal;
  const draft = useFormDraft('addGoal');

  const [name, setName] = useState('');
  const [target, setTarget] = useState('');
  const [icon, setIcon] = useState('🎯');
  const [deadline, setDeadline] = useState('');
  const [memberIds, setMemberIds] = useState([]);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!name.trim()) errs.name = t('goals.form.nameRequired');
    if (!target || Number(target) <= 0) errs.target = t('goals.form.targetRequired');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    const data = {
      name: name.trim(),
      target: Number(target),
      icon,
      memberIds,
      ...(deadline ? { deadline } : {}),
    };
    if (isEdit) {
      updateGoal(goal.id, data);
    } else {
      addGoal({ ...data, saved: 0 });
    }
    draft.clear();
    handleClose();
  };

  const handleClose = () => {
    setName('');
    setTarget('');
    setIcon('🎯');
    setDeadline('');
    setMemberIds([]);
    setErrors({});
    onClose();
  };

  useEffect(() => {
    if (!open) return;
    if (goal) {
      setName(goal.name);
      setTarget(String(goal.target));
      setIcon(goal.icon);
      setDeadline(goal.deadline || '');
      setMemberIds(goal.memberIds || []);
    } else {
      const saved = draft.load();
      if (saved) {
        setName(saved.name || '');
        setTarget(saved.target || '');
        setIcon(saved.icon || '🎯');
        setDeadline(saved.deadline || '');
        setMemberIds(saved.memberIds || []);
      }
    }
    setErrors({});
  }, [goal, open]);

  // Auto-save draft
  useEffect(() => {
    if (!open || isEdit) return;
    draft.save({ name, target, icon, deadline, memberIds });
  }, [open, isEdit, name, target, icon, deadline, memberIds]);

  return (
    <Modal open={open} onClose={handleClose} title={isEdit ? t('common.edit') : t('goals.addNew')} footer={
      <Button className="w-full" onClick={handleSave}>
        {t('common.save')}
      </Button>
    }>
      <div className="space-y-4">
        {/* Emoji picker */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('goals.form.icon')}
          </label>
          <div className="flex flex-wrap gap-2">
            {EMOJI_OPTIONS.map((e) => (
              <button
                key={e}
                onClick={() => setIcon(e)}
                className={`w-10 h-10 rounded-xl text-xl flex items-center justify-center transition-all ${
                  icon === e
                    ? 'bg-brand-50 dark:bg-brand-500/10 ring-2 ring-brand-500'
                    : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {e}
              </button>
            ))}
          </div>
        </div>

        {/* Name */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('goals.form.name')}
          </label>
          <input
            type="text"
            className={`input ${errors.name ? 'border-danger-500' : ''}`}
            placeholder={t('goals.form.namePlaceholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {errors.name && <p className="text-xs text-danger-500 mt-1">{errors.name}</p>}
        </div>

        {/* Target amount */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('goals.form.targetAmount')}
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl font-bold text-gray-300">$</span>
            <CurrencyInput
              placeholder="0"
              value={target}
              onChange={setTarget}
              className={`w-full pl-10 pr-4 py-3 text-lg font-bold input ${
                errors.target ? 'border-danger-500' : ''
              }`}
            />
          </div>
          {errors.target && <p className="text-xs text-danger-500 mt-1">{errors.target}</p>}
        </div>

        {/* Deadline (optional) */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('goals.form.deadline')}
          </label>
          <input
            type="date"
            className="input"
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
          />
        </div>

        {/* Member association */}
        <MemberMultiSelect
          value={memberIds}
          onChange={setMemberIds}
        />

      </div>
    </Modal>
  );
}
